import { useContext, useEffect, useState } from "react";
import UserContext from "../../Context/UserProvider";
import { useApiService } from "../../services/ApiService/ApiService";
import ScenarioSummaryBarChart from "../../components/fleet/FleetInsightComponent/ScenarioOverviewCompnents/ScenarioSummaryBarChart";
import ScenarioSummaryPieChart from "../../components/fleet/FleetInsightComponent/ScenarioOverviewCompnents/ScenarioSummaryPieChart";
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import DashboardCard from "components/dashboard/common/DashboardCard";
import DashboardCardRow from "components/dashboard/common/DashboardCardRow";
import SummarizeIcon from '@mui/icons-material/Summarize';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import BarChartIcon from '@mui/icons-material/BarChart';
import { Stack } from "@mui/material";
import toast from 'react-hot-toast';

function BusinessInsightPage(props: any) {
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [businessInsightData, setBusinessInsightData] = useState() as any;

    async function fetchBusinessInsight() {
        try {
            const response = await fetchWithAuth('/business/insight/get', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    businessUuid: userInfo.businessUuid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch business insight data');
            }

            const res_json = await response.json();
            res_json.categoryPercentages = res_json.categoryPercentages.map((e: any) => ({ 
                ...e, 
                percentage: Math.round(e.percentage * 100) 
            }));
            
            setBusinessInsightData(res_json);
            setIsError(false);
        } catch (error) {
            console.error('Error fetching business insight:', error);
            setIsError(true);
            toast.error('Failed to fetch business insight data');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchBusinessInsight();
    }, []);

    if (loading) {
        return (
            <Box 
                component="section" 
                sx={{ 
                    padding: '24px',
                    flex: 1,
                    overflow: 'auto',
                    width: '100%'
                }}
            >
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box 
                component="section" 
                sx={{ 
                    padding: '24px',
                    flex: 1,
                    overflow: 'auto',
                    width: '100%'
                }}
            >
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography color="error">Failed to load business insight data</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box 
            component="section"
            sx={{ 
                padding: '24px',
                flex: 1,
                overflow: 'auto',
                width: '100%'
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12} md={6} xl={6}>
                    <DashboardCard
                        title="Sampled Data"
                        icon={<SummarizeIcon />}
                    >
                        <Stack spacing={2}>
                            <DashboardCardRow
                                title="Number of vehicles"
                                value={Math.floor(businessInsightData.numberOfVehicles)}
                            />
                            <DashboardCardRow
                                title="Number of days"
                                value={Math.floor(businessInsightData.totalSampleDays)}
                            />
                            <DashboardCardRow
                                title="Total distance of ICE journeys"
                                value={`${Math.floor(businessInsightData.totalDistance).toLocaleString()} ${userInfo.userProfile.distanceUnitShortDisplayName}`}
                            />
                        </Stack>
                    </DashboardCard>
                </Grid>

                <Grid item xs={12} md={6} xl={6}>
                    <DashboardCard
                        title="Journeys"
                        icon={<LocationOnIcon />}
                    >
                        <Stack spacing={2}>
                            <DashboardCardRow
                                title="Total EV Journey Distance"
                                value={`${Math.floor(businessInsightData.totalEvDistance).toLocaleString()} ${userInfo.userProfile.distanceUnitShortDisplayName}`}
                            />
                            <DashboardCardRow
                                title="Average distance per day"
                                value={`${Math.floor(businessInsightData.averageDistancePerDay).toLocaleString()} ${userInfo.userProfile.distanceUnitShortDisplayName}`}
                            />
                            <DashboardCardRow
                                title="Average distance per journey"
                                value={`${Math.floor(businessInsightData.averageJourneyDistance).toLocaleString()} ${userInfo.userProfile.distanceUnitShortDisplayName}`}
                            />
                        </Stack>
                    </DashboardCard>
                </Grid>

                <Grid item xs={12} md={6} xl={4}>
                    <DashboardCard
                        title="Recommendation Category % breakdown"
                        icon={<DonutLargeIcon />}
                    >
                        <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <ScenarioSummaryPieChart
                                total={businessInsightData.numberOfVehicles}
                                unit={"Total Vehicles"}
                                fleetScenarioUuid=""
                                fleetScenarioNumber=""
                                title={""}
                                height={400}
                                width={400}
                                data={businessInsightData.categoryPercentages}
                            />
                        </Box>
                    </DashboardCard>
                </Grid>

                <Grid item xs={12} md={6} xl={4}>
                    <DashboardCard
                        title="Percentage of Journeys not requiring an enroute charge"
                        icon={<DonutLargeIcon />}
                    >
                        <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <ScenarioSummaryPieChart
                                total={businessInsightData.totalJourneys}
                                fleetScenarioUuid=""
                                unit={"Annual Journeys"}
                                title={""}
                                height={400}
                                width={400}
                                data={[
                                    { name: "Zero Charges", percentage: Math.round(businessInsightData.percentageJourneysNotRequiringEnRouteCharge * 100) },
                                    { name: "Multiple Charges", percentage: Math.round(businessInsightData.percentageJourneysRequiringMultiCharge * 100) },
                                    { name: "Single Charges", percentage: Math.round(businessInsightData.percentageJourneysRequiringSingleCharge * 100) },
                                ]}
                            />
                        </Box>
                    </DashboardCard>
                </Grid>

                <Grid item xs={12} md={6} xl={4}>
                    <DashboardCard
                        title="Energy v Fuel usage price comparison"
                        icon={<BarChartIcon />}
                    >
                        <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <ScenarioSummaryBarChart
                                fleetScenarioUuid=""
                                data={[
                                    { name: "Energy", price: Math.round(businessInsightData.annualCostSavingElectricity) },
                                    { name: "Fuel", price: Math.round(businessInsightData.annualCostSavingFuel) },
                                ]}
                            />
                        </Box>
                    </DashboardCard>
                </Grid>
            </Grid>
        </Box>
    );
}

export default BusinessInsightPage;
